import React, { useMemo } from 'react'
import { Button, Empty, Space, Tooltip, Typography } from 'antd'
import { CloudDownloadOutlined, PlusOutlined, FilterOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { toggleAddFilesModal, assignmentActions as assignmentSliceActions } from '../../../../../store/slices/assignment.slice'
import { useAssignmentAccess } from '../../../components/access/Access'
import { Perm } from '../../../../../constants/assignment-permissions'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import userSelectors from '../../../../../store/selectors/user.selectors'
import useQueryParamSetter from '../../../../../hooks/useQueryParamSetter'
import useQueryParams from '../../../../../hooks/useQueryParams'
import { FilterParamNames } from '../../../../../constants/assignment-filter-params'
import {
    ASSIGNEE_IDS_PARAM,
    CONFIDENCE_LEVELS_PARAM,
    FLAGGED_FOR_MYSELF_PARAM,
    FLAGGED_PARAM,
    MISMATCH_PARAM,
    REVIEW_STATUS_PARAM,
    SUBMISSION_SEARCH_PARAM,
} from '../../../../../constants/query-params'

const { Text } = Typography

const SubmissionsEmptyState = ({ totalSubmissionsBeforeFilter = 0 }) => {
    const dispatch = useDispatch()
    const setQueryParam = useQueryParamSetter()
    
    // Permission check for adding/importing submissions
    const { hasAccess: hasAddingFilesPermission } = useAssignmentAccess([Perm.CAN_EDIT])
    
    // LMS availability checks
    const lmsEnabled = useSelector(assignmentSelectors.lmsEnabled)
    const isLmsConfigurator = useSelector(userSelectors.isLmsConfigurator)
    const isSupervisor = useSelector(assignmentSelectors.isSupervisor)
    
    // Check URL params to detect if any filters are active
    // This is needed because some filters (escalated, search) are applied at API level
    const filterParams = useQueryParams(FilterParamNames)
    const hasActiveFilters = useMemo(() => {
        const [mismatch, flagged, reviewStatus, confidenceLevels, flaggedForMyself] = filterParams
        
        if (mismatch === 'true') return true
        if (flagged === 'true') return true
        if (flaggedForMyself === 'true') return true
        if (reviewStatus && reviewStatus !== 'any') return true
        if (confidenceLevels) return true
        
        return false
    }, [filterParams])
    
    // Filtered empty state if: filters are active OR submissions exist but none visible
    const isFilteredEmptyState = hasActiveFilters || totalSubmissionsBeforeFilter > 0
    
    // Determine LMS button disabled state and tooltip
    const isLmsDisabled = !lmsEnabled || !isLmsConfigurator || isSupervisor
    const lmsDisabledReason = useMemo(() => {
        if (!isLmsConfigurator) return 'LMS integration is not configured for your account'
        if (!lmsEnabled) return 'LMS is not enabled for this assignment'
        if (isSupervisor) return 'Supervisors cannot import submissions'
        return null
    }, [lmsEnabled, isLmsConfigurator, isSupervisor])
    
    const handleImportFromLms = () => {
        dispatch(assignmentSliceActions.setShowLmsModal(true))
    }
    
    const handleAddManually = () => {
        dispatch(toggleAddFilesModal())
    }
    
    const handleClearFilters = () => {
        setQueryParam({
            [MISMATCH_PARAM]: undefined,
            [FLAGGED_PARAM]: undefined,
            [FLAGGED_FOR_MYSELF_PARAM]: undefined,
            [REVIEW_STATUS_PARAM]: undefined,
            [CONFIDENCE_LEVELS_PARAM]: undefined,
            [ASSIGNEE_IDS_PARAM]: undefined,
            [SUBMISSION_SEARCH_PARAM]: undefined,
        })
    }
    
    // Filtered empty state - show clear filters CTA
    if (isFilteredEmptyState) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <Space direction="vertical" size={8}>
                        <Text strong>No submissions match your filters</Text>
                        <Text type="secondary">Try adjusting your filter criteria</Text>
                    </Space>
                }
            >
                <Button 
                    type="primary" 
                    icon={<FilterOutlined />}
                    onClick={handleClearFilters}
                >
                    Clear filters
                </Button>
            </Empty>
        )
    }
    
    // True empty state - show add/import CTAs
    // Only show action buttons if user has permission
    if (!hasAddingFilesPermission) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <Space direction="vertical" size={8}>
                        <Text strong>No submissions yet</Text>
                        <Text type="secondary">Contact an administrator to add submissions</Text>
                    </Space>
                }
            />
        )
    }
    
    return (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                <Space direction="vertical" size={8}>
                    <Text strong>No submissions yet</Text>
                    <Text type="secondary">Add submissions manually or import from your LMS</Text>
                </Space>
            }
        >
            <Space size={12}>
                <Tooltip title={lmsDisabledReason}>
                    <Button
                        type="primary"
                        icon={<CloudDownloadOutlined />}
                        onClick={handleImportFromLms}
                        disabled={isLmsDisabled}
                    >
                        Import from LMS
                    </Button>
                </Tooltip>
                <Button
                    icon={<PlusOutlined />}
                    onClick={handleAddManually}
                >
                    Add manually
                </Button>
            </Space>
        </Empty>
    )
}

export default SubmissionsEmptyState

